import { useEffect, useMemo, useState } from "react";
import {
  useQpmCertificateCustomerAddMutation,
  useQpmCertificateHttpCreateMutation,
  useQpmCertificateHttpListQuery,
} from "@/services/backend/qpm/certificates/api/certificate-api";
import {
  useQpmOfferAddCertificateToOfferMutation,
  useQpmOfferRemoveCertificateFromOfferMutation,
  useQpmOfferShowQuery,
  useQpmOfferUpdateOfferCertificateMutation,
} from "@/services/backend/qpm/offers/api/offer-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import { useToast } from "@/shared/hooks/use-toast";
import { useActiveCompany } from "@/shared/lib/active-company/active-company-provider";
import { Button } from "@/shared/components/ui/button";
import { Checkbox } from "@/shared/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shared/components/ui/command";
import { ChevronsUpDown, X } from "lucide-react";
import t from "@/lang/lang";

export function OfferCertificatesAdditionalInfo({
  offerId,
  customerId,
}: {
  offerId: string;
  customerId: string;
}) {
  const { toast } = useToast();
  const { companyId } = useActiveCompany();

  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState<string>("");

  const {
    data: offer,
    error: errorOffer,
    isLoading: isLoadingOffer,
  } = useQpmOfferShowQuery({ id: offerId }, { skip: !offerId });

  const {
    data: qpmCertificateList,
    error: errorCertificates,
    isLoading: isLoadingCertificates,
  } = useQpmCertificateHttpListQuery({
    company: { active: true, values: [companyId] },
  });

  const [createCertificate, createState] =
    useQpmCertificateHttpCreateMutation();

  const [addCustomerCertificate, addCustomerState] =
    useQpmCertificateCustomerAddMutation();

  const [addOfferCertificate, addOfferState] =
    useQpmOfferAddCertificateToOfferMutation();
  const [removeOfferCertificate, removeOfferState] =
    useQpmOfferRemoveCertificateFromOfferMutation();
  const [updateOfferCertificate, updateOfferState] =
    useQpmOfferUpdateOfferCertificateMutation();

  useEffect(() => {
    const err =
      createState.error ||
      addCustomerState.error ||
      addOfferState.error ||
      removeOfferState.error ||
      updateOfferState.error;

    if (!err) {
      return;
    }

    toast({ ...parseRTKQueryError(err) });
    createState.reset();
    addCustomerState.reset();
    addOfferState.reset();
    removeOfferState.reset();
    updateOfferState.reset();
  }, [
    addCustomerState,
    addOfferState,
    createState,
    removeOfferState,
    toast,
    updateOfferState,
  ]);

  const selectedIds = useMemo(
    () =>
      new Set(offer?.certificateSnapshot?.certificates?.map((c) => c.id) ?? []),
    [offer?.certificateSnapshot?.certificates],
  );

  const availableCertificates = useMemo(() => {
    const items = qpmCertificateList?.items ?? [];
    return items.filter((it) => !selectedIds.has(it.id));
  }, [qpmCertificateList?.items, selectedIds]);

  const selectedCertificates = useMemo(() => {
    const selected = offer?.certificateSnapshot?.certificates ?? [];
    const map = new Map(
      (qpmCertificateList?.items ?? []).map((c) => [c.id, c]),
    );

    return selected.map((cert) => ({
      id: cert.id,
      name: cert.name,
      alwaysDisplay:
        cert.alwaysDisplay ?? map.get(cert.id)?.alwaysDisplay ?? false,
    }));
  }, [offer?.certificateSnapshot?.certificates, qpmCertificateList?.items]);

  const handleAddExisting = async (certificateId: string) => {
    if (!offer || offer.immutable) return;
    if (customerId) {
      await addCustomerCertificate({ customerId, certificateId }).unwrap();
    }
    await addOfferCertificate({ offerId, certificateId }).unwrap();

    setSearchValue("");
    setOpen(false);
  };

  const handleCreateAndAdd = async () => {
    if (!offer || offer.immutable) return;
    const name = searchValue.trim();
    if (!name) {
      return;
    }

    const created = await createCertificate({
      companyId,
      name,
      alwaysDisplay: true,
    }).unwrap();

    if (customerId) {
      await addCustomerCertificate({
        customerId,
        certificateId: created.id,
      }).unwrap();
    }

    await addOfferCertificate({ offerId, certificateId: created.id }).unwrap();

    setSearchValue("");
    setOpen(false);
  };

  const handleRemove = async (certificateId: string) => {
    if (!offer || offer.immutable) return;
    await removeOfferCertificate({ offerId, certificateId }).unwrap();
  };

  const handleToggleAlwaysDisplay = async (
    certificateId: string,
    next: boolean,
    name: string,
  ) => {
    if (!offer || offer.immutable) return;
    await updateOfferCertificate({
      offerId,
      certificateId,
      alwaysDisplay: next,
      name,
    }).unwrap();
  };

  if (isLoadingOffer || isLoadingCertificates) {
    return (
      <div className="text-muted-foreground animate-pulse">
        {t("Lade Daten...")}
      </div>
    );
  }

  if (errorOffer) {
    return <RTKQueryErrorAlert error={errorOffer} />;
  }

  if (errorCertificates) {
    return <RTKQueryErrorAlert error={errorCertificates} />;
  }

  if (!offer) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="min-w-65 justify-between"
              disabled={offer.immutable}
            >
              {t("Zertifikat wählen oder anlegen")}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="min-w-65 p-0" side="bottom" align="start">
            <Command>
              <CommandInput
                placeholder={t("Suchen oder anlegen...")}
                value={searchValue}
                onValueChange={setSearchValue}
                disabled={offer.immutable}
                onKeyDown={(e) => {
                  if (offer.immutable) {
                    return;
                  }
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleCreateAndAdd().catch(() => undefined);
                  }
                }}
              />
              <CommandList>
                <CommandEmpty>
                  <div className="flex flex-col gap-3 px-6 text-sm">
                    <span>{t("Kein Zertifikat gefunden.")}</span>
                    <Button
                      type="button"
                      onClick={() => {
                        handleCreateAndAdd().catch(() => undefined);
                      }}
                      disabled={offer.immutable || !searchValue.trim()}
                    >
                      {t("Hinzufügen")}
                    </Button>
                  </div>
                </CommandEmpty>
                <CommandGroup heading={t("Verfügbare Zertifikate")}>
                  {availableCertificates.map((cert) => (
                    <CommandItem
                      key={cert.id}
                      disabled={offer.immutable}
                      onSelect={() => {
                        handleAddExisting(cert.id).catch(() => undefined);
                      }}
                    >
                      {cert.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      <div className="flex flex-col gap-2">
        {selectedCertificates.map((cert) => (
          <div
            key={cert.id}
            className="flex items-center justify-between gap-2 rounded-md border px-3 py-2"
          >
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                title={t("Entfernen")}
                disabled={offer.immutable}
                onClick={() => {
                  handleRemove(cert.id).catch(() => undefined);
                }}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
              <div className="font-medium">{cert.name}</div>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                checked={cert.alwaysDisplay}
                disabled={offer.immutable}
                onCheckedChange={(checked) => {
                  handleToggleAlwaysDisplay(
                    cert.id,
                    checked === true,
                    cert.name,
                  ).catch(() => undefined);
                }}
              />
              <div className="text-muted-foreground text-sm">
                {t("Standardmäßig anzeigen")}
              </div>
            </div>
          </div>
        ))}

        {selectedCertificates.length === 0 && (
          <div className="text-muted-foreground text-sm">
            {t("Keine Zertifikate ausgewählt.")}
          </div>
        )}
      </div>
    </div>
  );
}
